import "cropperjs/dist/cropper.css";

import {
    CheckOutlined,
    CloseOutlined,
    RotateLeftOutlined,
    RotateRightOutlined,
    ScissorOutlined,
} from "@ant-design/icons";
import Cropper from "cropperjs";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import {
    StyledActionsContainer,
    StyledTextButton,
    StyledViewContainer,
} from "../../Styled";
import { StyledCropperContainer, StyledIconButton } from "./Styled";

interface EditingModeProps {
    capturedImage: string;
    onCancel: () => void;
    onSave: (blob: Blob) => void;
}

export const EditingMode = memo(
    ({ capturedImage, onCancel, onSave }: EditingModeProps) => {
        const imgRef = useRef<HTMLImageElement | null>(null);
        const cropperRef = useRef<Cropper | null>(null);
        const hasEditsRef = useRef(false);

        const [isCropMode, setIsCropMode] = useState(false);

        useEffect(() => {
            const imgEl = imgRef.current;
            if (!imgEl) return;

            hasEditsRef.current = false;
            setIsCropMode(false);

            if (cropperRef.current) {
                cropperRef.current.destroy();
                cropperRef.current = null;
            }

            const cropper = new Cropper(imgEl, {
                viewMode: 1,
                dragMode: "move",
                autoCrop: false,
                background: false,
                movable: true,
                zoomable: true,
                rotatable: true,
                scalable: false,
                zoomOnWheel: true,
                responsive: true,
                restore: false,
                ready() {
                    cropperRef.current = cropper;
                },
            });

            return () => {
                cropper.destroy();
                cropperRef.current = null;
            };
        }, [capturedImage]);

        const handleToggleCrop = useCallback(() => {
            const cropper = cropperRef.current;
            if (!cropper) return;

            if (!isCropMode) {
                cropper.crop();

                const canvasData = cropper.getCanvasData();

                cropper.setCropBoxData({
                    left: canvasData.left + canvasData.width * 0.05,
                    top: canvasData.top + canvasData.height * 0.1,
                    width: canvasData.width * 0.9,
                    height: canvasData.height * 0.9,
                });

                hasEditsRef.current = true;
                setIsCropMode(true);
                return;
            }

            cropper.clear();
            hasEditsRef.current = true;
            setIsCropMode(false);
        }, [isCropMode]);

        const handleRotateLeft = useCallback(() => {
            const cropper = cropperRef.current;
            if (!cropper) return;

            cropper.rotate(-90);
            hasEditsRef.current = true;
        }, []);

        const handleRotateRight = useCallback(() => {
            const cropper = cropperRef.current;
            if (!cropper) return;

            cropper.rotate(90);
            hasEditsRef.current = true;
        }, []);

        const handleSaveOriginal = useCallback(async () => {
            const response = await fetch(capturedImage);
            const blob = await response.blob();
            onSave(blob);
        }, [capturedImage, onSave]);

        const handleSaveCanvas = useCallback(() => {
            const cropper = cropperRef.current;
            if (!cropper) return;

            const imageData = cropper.getImageData();

            const canvas = cropper.getCroppedCanvas({
                width: imageData.naturalWidth,
                height: imageData.naturalHeight,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: "high",
            });

            canvas.toBlob(
                (blob) => {
                    if (blob) onSave(blob);
                },
                "image/jpeg",
                1,
            );
        }, [onSave]);

        const handleSave = useCallback(async () => {
            const cropper = cropperRef.current;
            if (!cropper) return;

            if (!hasEditsRef.current) {
                await handleSaveOriginal();
                return;
            }

            handleSaveCanvas();
        }, [handleSaveCanvas, handleSaveOriginal]);

        return (
            <>
                <StyledViewContainer>
                    <StyledCropperContainer>
                        <img
                            ref={imgRef}
                            src={capturedImage}
                            alt="Editing"
                            crossOrigin="anonymous"
                            style={{ display: "block", maxWidth: "100%" }}
                        />
                    </StyledCropperContainer>
                </StyledViewContainer>

                <StyledActionsContainer $placement="top" $offset={8} gap={8}>
                    <StyledIconButton
                        icon={<RotateLeftOutlined />}
                        onClick={handleRotateLeft}
                        type="text"
                    />

                    <StyledIconButton
                        icon={<RotateRightOutlined />}
                        onClick={handleRotateRight}
                        type="text"
                    />

                    <StyledIconButton
                        icon={<ScissorOutlined />}
                        onClick={handleToggleCrop}
                        type={isCropMode ? "primary" : "text"}
                    />
                </StyledActionsContainer>

                <StyledActionsContainer
                    justify="center"
                    align="center"
                    gap={32}
                >
                    <StyledTextButton
                        size="large"
                        onClick={onCancel}
                        type="text"
                        icon={<CloseOutlined />}
                    >
                        Скасувати
                    </StyledTextButton>

                    <StyledTextButton
                        icon={<CheckOutlined />}
                        onClick={handleSave}
                        size="large"
                        type="primary"
                    >
                        Зберегти
                    </StyledTextButton>
                </StyledActionsContainer>
            </>
        );
    },
);

EditingMode.displayName = "EditingMode";
